/**
 * AGREEMENT INTERCEPTOR
 *
 * Axios interceptor that handles agreement-related errors globally.
 * When a 403 response with AGREEMENT_REQUIRED code is received,
 * it triggers the agreement modal.
 *
 * USAGE:
 *   import { setupAgreementInterceptor } from './utils/agreementInterceptor';
 *
 *   // In App.js or index.js
 *   setupAgreementInterceptor();
 *
 * CREATED: July 2025
 */

import axios from 'axios';

// Global state for agreement modal
let showAgreementModalCallback = null;
let pendingAgreementsCallback = null;

/**
 * Register callbacks for showing the agreement modal
 * Called by AgreementChecker component
 */
export const registerAgreementCallbacks = (showModal, setPendingAgreements) => {
  showAgreementModalCallback = showModal;
  pendingAgreementsCallback = setPendingAgreements;
};

/**
 * Unregister callbacks (on unmount)
 */
export const unregisterAgreementCallbacks = () => {
  showAgreementModalCallback = null;
  pendingAgreementsCallback = null;
};

/**
 * Agreement error codes that trigger the modal
 */
const AGREEMENT_ERROR_CODES = [
  'AGREEMENT_REQUIRED',
  'SELLER_AGREEMENT_REQUIRED',
  'TOS_REQUIRED'
];

/**
 * Setup the Axios interceptor
 */
export const setupAgreementInterceptor = () => {
  axios.interceptors.response.use(
    // Success - pass through
    (response) => response,

    // Error - check for agreement errors
    (error) => {
      const errorCode = error.response?.data?.code;
      const pendingAgreements = error.response?.data?.pendingAgreements;
      const singleAgreement = error.response?.data?.agreement;

      // Check if this is an agreement-related error
      if (error.response?.status === 403 && AGREEMENT_ERROR_CODES.includes(errorCode)) {
        console.log('Agreement required:', errorCode);

        // Build agreements array
        let agreements = [];
        if (pendingAgreements && Array.isArray(pendingAgreements)) {
          agreements = pendingAgreements;
        } else if (singleAgreement) {
          agreements = [singleAgreement];
        }

        // Trigger the modal if callbacks are registered
        if (showAgreementModalCallback && agreements.length > 0) {
          if (pendingAgreementsCallback) {
            pendingAgreementsCallback(agreements);
          }
          showAgreementModalCallback(true);
        }

        // Modify the error to include a flag for easier handling
        error.isAgreementError = true;
        error.agreementCode = errorCode;
        error.requiredAgreements = agreements;
      }

      return Promise.reject(error);
    }
  );
};

/**
 * Check if an error is an agreement error
 * Useful for conditional handling in catch blocks
 */
export const isAgreementError = (error) => {
  return error?.isAgreementError === true ||
    AGREEMENT_ERROR_CODES.includes(error?.response?.data?.code);
};

/**
 * Get user-friendly message for agreement errors
 */
export const getAgreementErrorMessage = (error) => {
  const code = error?.response?.data?.code || error?.agreementCode;

  switch (code) {
    case 'SELLER_AGREEMENT_REQUIRED':
      return 'Please accept the Seller Agreement to continue.';
    case 'TOS_REQUIRED':
      return 'Please accept the Terms of Service to continue.';
    case 'AGREEMENT_REQUIRED':
    default:
      return 'Please accept the required agreements to continue.';
  }
};
